/**
 * DilaCode's software products: the systems we build, run and sell.
 * Single source of truth for /products index, detail pages, nav and SEO.
 * Copy lives in the page components; this file holds identity + metadata.
 */

export type ProductSlug = 'dilacrm' | 'dilaerp' | 'dilamenu';

export interface Product {
  slug: ProductSlug;
  /** Mono index shown on the index page ('01'). */
  index: string;
  name: string;
  /** Per-page accent — locked per DESIGN.md. */
  accent: string;
  /** One-line positioning used on cards and meta. */
  tagline: string;
  /** Two-sentence blurb for the index page. */
  blurb: string;
  /** Three mono stat chips on the index page. */
  chips: string[];
  /** Index-page visual. */
  image: string;
  imageAlt: string;
  /** Public URL of the live product (display + link). */
  domain: string;
  liveUrl: string;
  liveLabel: string;
  seo: {
    title: string;
    description: string;
    keywords: string[];
  };
}

export const products: Product[] = [
  {
    slug: 'dilacrm',
    index: '01',
    name: 'DilaCRM',
    accent: '#6b93ff',
    tagline: 'One inbox for customer conversations.',
    blurb:
      'WhatsApp, Instagram, Messenger, Telegram, TikTok and email in one inbox. AI drafts the reply in Kurdish, Arabic or English, you approve, it sends.',
    chips: ['6 channels', 'Human approval', '3 AI languages'],
    image: '/images/products/dilacrm/hero-approval.webp',
    imageAlt:
      'DilaCRM inbox showing an AI-suggested reply awaiting human approval, with Send, Edit and Don’t send actions.',
    domain: 'crm.dilaweb.com',
    liveUrl: 'https://crm.dilaweb.com',
    liveLabel: 'Live inbox',
    seo: {
      title: 'DilaCRM: AI inbox for WhatsApp, Instagram, Telegram and more',
      description:
        'DilaCRM brings WhatsApp, Instagram, Messenger, Telegram, TikTok and email into one inbox, with a built-in CRM and AI-assisted replies in Kurdish, Arabic and English.',
      keywords: [
        'DilaCRM',
        'SalesMate',
        'AI inbox Iraq',
        'WhatsApp CRM Kurdistan',
        'respond.io alternative',
        'Kurdish AI chatbot',
        'WhatsApp bulk campaigns',
        'comment automation Facebook Instagram',
        'Datacode',
      ],
    },
  },
  {
    slug: 'dilaerp',
    index: '02',
    name: 'DilaERP',
    accent: '#3cc984',
    tagline: 'The ERP that speaks Kurdish.',
    blurb:
      'Sales, stock, accounting, HR, payroll and POS in one simple system. Fifteen modules, three languages, self-hosted so your data stays yours.',
    chips: ['15 modules', 'Self-hosted', '3 languages'],
    image: '/images/products/dilaerp/dashboard.webp',
    imageAlt:
      'DilaERP dashboard with monthly revenue, money owed, overdue invoices and a 30-day revenue chart in IQD.',
    domain: 'app.datacode.app',
    liveUrl: 'https://app.datacode.app',
    liveLabel: 'Live demo',
    seo: {
      title: 'DilaERP: the ERP that speaks Kurdish',
      description:
        'DilaERP supports sales, inventory, accounting, HR, payroll, POS and CRM in Kurdish, Arabic and English. It includes reporting, an AI assistant and self-hosted deployment.',
      keywords: [
        'DilaERP',
        'Kurdish ERP',
        'ERP Iraq',
        'ERP Kurdistan',
        'ERPNext alternative',
        'Kurdish accounting software',
        'POS Iraq',
        'self-hosted ERP',
        'Datacode',
      ],
    },
  },
  {
    slug: 'dilamenu',
    index: '03',
    name: 'dila.menu',
    accent: '#d4a574',
    tagline: 'Digital menus and restaurant operations in one system.',
    blurb:
      'QR menus, WhatsApp and dine-in ordering, plus dila RMS for point of sale and kitchen operations.',
    chips: ['3 languages', 'Dine-in ordering', 'Restaurant POS'],
    image: '/images/products/dilamenu/menu-full.webp',
    imageAlt:
      'A dila.menu restaurant menu in the Kilim theme showing a chef’s signature Adana Kebab with IQD pricing and food photography.',
    domain: 'dila.menu',
    liveUrl: 'https://dila.menu',
    liveLabel: 'Live menus',
    seo: {
      title: 'dila.menu: digital menus and restaurant management for Kurdistan',
      description:
        'dila.menu provides trilingual QR menus, WhatsApp ordering, reservations and analytics. It works with dila RMS for table service, kitchen routing and local payment workflows.',
      keywords: [
        'dila.menu',
        'QR menu Kurdistan',
        'digital menu Iraq',
        'restaurant POS Iraq',
        'RMS restaurant management',
        'Talabat integration POS',
        'Kurdish restaurant software',
        'Datacode',
      ],
    },
  },
];

export function getProduct(slug: string): Product | undefined {
  return products.find((p) => p.slug === slug);
}
